import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import ToastManager from '../components/ToastManager/ToastManager';
import ReactConfetti from 'react-confetti';

const ToastContext = createContext();

// Export the context for test utilities  
export { ToastContext };

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [isConfettiActive, setIsConfettiActive] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  // Create a ref to hold the confetti timeout ID
  const confettiTimerRef = useRef(null);
  // Create a ref to track shown achievement IDs to prevent duplicates
  const shownAchievementIdsRef = useRef(new Set());

  // Effect to update window size for confetti
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Effect to prevent horizontal scrolling during confetti
  useEffect(() => {
    if (isConfettiActive) {
      document.body.style.overflowX = 'hidden';
    } else {
      document.body.style.overflowX = '';
    }
    return () => {
      document.body.style.overflowX = '';
    };
  }, [isConfettiActive]);

  const triggerConfetti = useCallback(() => {
    // Clear any existing timeout before setting a new one
    if (confettiTimerRef.current) {
      clearTimeout(confettiTimerRef.current);
    }

    setIsConfettiActive(true);
    
    // Store the new timeout ID in the ref
    confettiTimerRef.current = setTimeout(() => {
      setIsConfettiActive(false);
    }, 4000);
  }, []);

  const addToast = useCallback((toast) => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      ...toast,
      autoClose: toast.autoClose === true,
      autoCloseDelay: toast.autoCloseDelay || 5000
    };
    
    setToasts(prev => [...prev, newToast]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const clearShownAchievements = useCallback(() => {
    shownAchievementIdsRef.current.clear();
  }, []);

  const showAchievementToast = useCallback((achievement, options = {}) => {
    // Check if we've already shown this achievement
    if (shownAchievementIdsRef.current.has(achievement.id)) {
      return null;
    }

    // Mark this achievement as shown
    shownAchievementIdsRef.current.add(achievement.id);

    // Delay achievement confetti until after scroll completes
    setTimeout(() => {
      triggerConfetti();
    }, 800);

    return addToast({
      type: 'achievement',
      achievement,
      ...options
    });
  }, [addToast, triggerConfetti]);

  const value = {
    toasts,
    addToast,
    removeToast,
    clearAllToasts,
    clearShownAchievements,
    showAchievementToast
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {isConfettiActive && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 10000
        }}>
          {/* Left Cannon */}
          <ReactConfetti
            width={windowSize.width}
            height={windowSize.height}
            recycle={false}
            numberOfPieces={200}
            gravity={0.25}
            initialVelocityX={{ min: 0, max: 15 }}
            initialVelocityY={{ min: -25, max: -5 }}
            tweenDuration={100}
            confettiSource={{
              x: 0,
              y: windowSize.height * 0.8, // 80% down the viewport
              w: 0,
              h: 0,
            }}
            style={{ pointerEvents: 'none' }}
          />
          {/* Right Cannon - Mirrored */}
          <ReactConfetti
            width={windowSize.width}
            height={windowSize.height}
            recycle={false}
            numberOfPieces={200}
            gravity={0.25}
            initialVelocityX={{ min: -15, max: 0 }}
            initialVelocityY={{ min: -25, max: -5 }}
            tweenDuration={100}
            confettiSource={{
              x: windowSize.width,
              y: windowSize.height * 0.8, // 80% down the viewport
              w: 0,
              h: 0,
            }}
            style={{ pointerEvents: 'none' }}
          />
        </div>
      )}
      <ToastManager 
        toasts={toasts} 
        onRemoveToast={removeToast} 
      />
    </ToastContext.Provider>
  );
};